import { AnalysisSet } from '@paragon/contract-library';

import { adoptOptimizedDesign, optimizeActiveComponentDesign } from '../../store/actions/optimizationActions';
import {
  bulkUpgradeAndAnalyze,
  upgradeAndAnalyzeActiveComponentDesign,
} from '../../store/actions/paragonProcessActions';
import { getActiveAnalysisSet } from '../../store/selectors/getActiveAnalysisSetSelector';
import { getCommittedComponentDesigns } from '../../store/selectors/getComponentDesignsSelectors';
import { getLoadCasesForActiveComponentDesign } from '../../store/selectors/getLoadCasesForActiveComponentDesignSelector';
import { getOptimizationRunForActiveComponent } from '../../store/selectors/optimizationProgressSelectors';
import { clearLongRunningAnalysis } from '../../store/slices/longRunningOperationsSlice';
import { getTrussAnalysisStatus } from './contextTools';
import { findTrussByName, openTruss, requireString, waitForAppToSettle } from './helpers';
import { defineTool, ToolError } from './types';

const GOVERNING_OBJECT_COUNT = 5;

/**
 * What governs a truss's analysis, as Paragon reports it.
 */
export function summarizeAnalysisSet(analysisSet: AnalysisSet, loadCaseNames: readonly string[] = []) {
  // Analysis numbers its load cases from 1 in the truss's own order, as the properties panel reads them.
  const loadCase = (number: number) => (number > 0 ? { number, name: loadCaseNames[number - 1] ?? null } : null);
  const governing = Object.values(analysisSet.objectCapacities ?? {})
    .sort((a, b) => b.capacity - a.capacity)
    .slice(0, GOVERNING_OBJECT_COUNT)
    .map(({ objectChecked, capacity }) => ({
      object: objectChecked.name,
      type: objectChecked.type,
      capacityRatio: capacity,
    }));

  return {
    capacityRatio: analysisSet.capacity,
    passes: analysisSet.capacity <= 1 && analysisSet.errors.length === 0,
    csi: {
      topChord: { csi: analysisSet.csi.topChord.csi, loadCase: loadCase(analysisSet.csi.topChord.loadCase) },
      bottomChord: { csi: analysisSet.csi.bottomChord.csi, loadCase: loadCase(analysisSet.csi.bottomChord.loadCase) },
      web: { csi: analysisSet.csi.web.csi, loadCase: loadCase(analysisSet.csi.web.loadCase) },
    },
    jsi: analysisSet.jsi,
    // In pounds, as the drawing lists them: each bearing joint's largest down and up reaction, with its load case.
    reactions: analysisSet.maximumBearingReactions.map((reaction) => ({
      joint: reaction.jointName,
      down:
        reaction.downForce == null
          ? null
          : { lb: Math.round(reaction.downForce.force), loadCase: loadCase(reaction.downForce.loadCaseIndex) },
      up:
        reaction.upForce == null
          ? null
          : { lb: Math.round(reaction.upForce.force), loadCase: loadCase(reaction.upForce.loadCaseIndex) },
      horizontal: {
        lb: Math.round(reaction.horizontalForce.force),
        loadCase: loadCase(reaction.horizontalForce.loadCaseIndex),
      },
      bearingWidth: reaction.actualBearingWidth,
      requiredBearingWidth: reaction.requiredBearingWidth,
    })),
    governing,
    failures: (analysisSet.objectCheckFailureSummaries ?? []).map((failure) => ({
      object: failure.objectChecked.name,
      check: failure.checkFailureSummary,
      capacityRatio: failure.capacityRatio,
    })),
    errors: analysisSet.errors,
    warnings: analysisSet.warnings,
  };
}

export const engineeringTools = [
  defineTool<{ trusses?: string[] }>({
    name: 'upgrade_and_analyze',
    description:
      'Runs Upgrade and Analyze: upgrades members and plates, then analyzes. One truss runs as the Analyze button ' +
      'does; several, or the default of every truss in the project, run as Analyze All does, with jacks before the ' +
      'girders that carry them. Returns each truss’s result.',
    inputSchema: {
      type: 'object',
      properties: { trusses: { type: 'array', items: { type: 'string' }, description: 'Truss names.' } },
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      // A single "truss", as most tools take, means that truss: never let it fall through to every truss.
      const single = (input as { truss?: unknown }).truss;
      const names = input.trusses ?? (typeof single === 'string' ? [single] : null);
      const componentDesigns =
        names != null
          ? names.map((name) => findTrussByName(getState(), name))
          : getCommittedComponentDesigns(getState());
      if (componentDesigns.length === 0) throw new ToolError('There are no trusses to analyze.');

      // One truss goes through the Analyze button's action, which shows a spinner; Analyze All's opens the project's
      // analysis dialog, which is out of place for a single truss.
      if (componentDesigns.length === 1) {
        const componentDesign = await openTruss(dispatch, getState, componentDesigns[0].component.name);
        const analyzedBefore = getState().componentDesignTimestamps[componentDesign.guid]?.lastAnalyzed;
        const startedAt = Date.now();

        await dispatch(upgradeAndAnalyzeActiveComponentDesign());
        await waitForAppToSettle(getState);

        if (getState().componentDesignTimestamps[componentDesign.guid]?.lastAnalyzed === analyzedBefore) {
          // The app reports why as notifications; pass them on so the model can act on the reason.
          const reasons = getState()
            .notifications.snackbars.filter(
              (notification) =>
                notification.addedAt >= startedAt &&
                (notification.options.variant === 'error' || notification.options.variant === 'warning'),
            )
            .map((notification) => notification.message);
          throw new ToolError(
            `${componentDesign.component.name} wasn’t analyzed. ` +
              (reasons.length > 0
                ? `The app said: ${reasons.join(' ')}`
                : 'The app gave no reason; the designer may be over their analysis quota.'),
          );
        }
        return {
          summary: {
            results: [
              { truss: componentDesign.component.name, ...getTrussAnalysisStatus(getState(), componentDesign.guid) },
            ],
          },
        };
      }

      await waitForAppToSettle(getState);
      await dispatch(bulkUpgradeAndAnalyze(componentDesigns.map(({ guid }) => guid)));
      await waitForAppToSettle(getState);

      const run = getState().longRunningOperations.analysis;
      if (run == null) throw new ToolError('The analysis didn’t start. The designer may be over their analysis quota.');

      const results = componentDesigns.map((componentDesign) => {
        const progress = run.componentDesigns[componentDesign.guid];
        return {
          truss: componentDesign.component.name,
          ...getTrussAnalysisStatus(getState(), componentDesign.guid),
          ...(progress?.currentStage === 'failed' ? { failed: true } : {}),
          messages: progress?.recordedMessages.map(({ type, message }) => `${type}: ${message}`) ?? [],
        };
      });

      // Close the progress dialog, as the designer would once it's done.
      dispatch(clearLongRunningAnalysis());
      return { summary: { canceled: run.canceled, results } };
    },
  }),

  defineTool<{ truss: string }>({
    name: 'get_analysis',
    description:
      'Reads a truss’s latest analysis: its capacity ratio, CSI by chord and web with the governing load case, bearing ' +
      'reactions (largest down, up, and horizontal, with required bearing widths), the members and plates closest ' +
      'to capacity, failed checks, errors, and warnings.',
    inputSchema: { type: 'object', properties: { truss: { type: 'string' } }, required: ['truss'] },
    changesProject: false,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      const analysisSet = getActiveAnalysisSet(getState());
      if (analysisSet == null || analysisSet.componentDesign.guid !== componentDesign.guid)
        throw new ToolError(`${componentDesign.component.name} hasn’t been analyzed yet.`);

      return {
        summary: {
          truss: componentDesign.component.name,
          ...getTrussAnalysisStatus(getState(), componentDesign.guid),
          ...summarizeAnalysisSet(
            analysisSet,
            getLoadCasesForActiveComponentDesign(getState()).map((loadCase) => loadCase.name),
          ),
        },
      };
    },
  }),

  defineTool<{ truss: string }>({
    name: 'start_optimization',
    description:
      'Starts optimizing a truss in the background, as the Optimization panel does. It takes a while; check on it ' +
      'later with get_optimization, and adopt the result with adopt_optimization.',
    inputSchema: { type: 'object', properties: { truss: { type: 'string' } }, required: ['truss'] },
    changesProject: false,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      // Optimization runs in the background already; the Assistant checks on it later rather than waiting.
      void dispatch(optimizeActiveComponentDesign());
      return { summary: { started: componentDesign.component.name } };
    },
  }),

  defineTool<{ truss: string }>({
    name: 'get_optimization',
    description: 'Checks on a truss’s optimization run: its status, progress, and the cost saving found so far.',
    inputSchema: { type: 'object', properties: { truss: { type: 'string' } }, required: ['truss'] },
    changesProject: false,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      const run = getOptimizationRunForActiveComponent(getState());
      if (run == null) throw new ToolError(`${componentDesign.component.name} has no optimization run.`);
      return {
        summary: {
          truss: componentDesign.component.name,
          status: run.status,
          progress: run.progress,
          costComparison: run.costComparison,
          adopted: run.adopted,
        },
      };
    },
  }),

  defineTool<{ truss: string }>({
    name: 'adopt_optimization',
    description: 'Makes the truss a finished optimization run found the truss’s design.',
    inputSchema: { type: 'object', properties: { truss: { type: 'string' } }, required: ['truss'] },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      const run = getOptimizationRunForActiveComponent(getState());
      if (run?.status !== 'completed') throw new ToolError('The optimization hasn’t finished.');

      await dispatch(adoptOptimizedDesign());
      await waitForAppToSettle(getState);
      return { summary: { adopted: componentDesign.component.name } };
    },
  }),
];
